// 11. パンフレット写真一覧／パンフレット出力（管理者向け・S7 実API）。参加者のみ・五十音順(kana)・3:4。
// 一覧＝サムネのカードグリッド。カードクリックで詳細モーダル、提出済＝「差し替え」/未提出＝「アップロード」。
// 同モーダル内でトリミング表示に切り替え（二重モーダルにしない）。保存＝代理アップロード（§1⑪：承認なし）。
// パンフレット出力＝CSV＋画像ZIP をDL → Illustrator テンプレ（縦8×横8）で配置（🟦11・RC-3=3:4）。
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  getRecitalPhotos,
  uploadRecitalPhoto,
  exportRecitalPhotos,
} from "../../../utils/recitalApi";
import "./recital.scss";

const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/photos" },
  { title: "Photos", path: "/recital/2026/photos" },
];

// パンフレット掲載比率＝3:4（縦長・RC-3 改）
const ASPECT = 3 / 4;

// 画像読み込み（クロップ用）
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });

// 切り抜き範囲(px)から 3:4 JPEG Blob を生成（会員側 profile-photo と同方式）
async function getCroppedBlob(src, area) {
  const img = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

// 生年月日(ISO) → 表示用 YYYY/MM/DD
const fmtBirthday = (iso) => (iso ? iso.replaceAll("-", "/") : "—");

export default function RecitalPhotoPage() {
  const [list, setList] = useState([]); // {customer_id, name, kana, birthday, age, studio, submitted, url, uploaded_by, submitted_at}
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null); // 右パネルで開いている参加者（customer_id）
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);

  // トリミングモーダル
  const [cropSrc, setCropSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 反映先 customer_id
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const onCropComplete = useCallback((_, px) => setAreaPixels(px), []);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");

  const notify = useCallback((text, type = "success") => {
    setMsgType(type);
    setMessage(text);
    setTimeout(() => setMessage(""), 3000);
  }, []);

  useEffect(() => {
    let alive = true;
    getRecitalPhotos()
      .then((d) => {
        if (!alive) return;
        setList(d.rows || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setLoading(false);
        notify(e.message || "読み込みに失敗しました", "error");
      });
    return () => {
      alive = false;
    };
  }, [notify]);

  const done = list.filter((p) => p.submitted).length;
  const pending = list.length - done;

  // backend が五十音順で返すため並びは保持。氏名/フリガナで絞り込みのみ。
  const rows = useMemo(
    () =>
      q === ""
        ? list
        : list.filter((p) => p.name.includes(q) || p.kana.includes(q)),
    [list, q],
  );

  const open = list.find((p) => p.customer_id === openId) || null;

  const closeModal = () => {
    setOpenId(null);
    setCropSrc(null);
    setCropTarget(null);
  };

  // 差し替え／アップロードのファイル選択。直接反映せず、まずトリミングモーダルを開く。
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !open) return;
    const targetId = open.customer_id;
    const isHeic =
      /image\/(heic|heif)/i.test(f.type) || /\.(heic|heif)$/i.test(f.name);
    let blob = f;
    try {
      if (isHeic) {
        const heic2any = (await import("heic2any")).default;
        const out = await heic2any({ blob: f, toType: "image/jpeg", quality: 0.9 });
        blob = Array.isArray(out) ? out[0] : out;
      }
    } catch {
      return;
    }
    setCropTarget(targetId);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
    setCropSrc(URL.createObjectURL(blob));
  };

  // トリミング確定：3:4 に切り抜き → 代理アップロード → 反映
  const confirmCrop = async () => {
    if (!cropSrc || !areaPixels || !cropTarget || saving) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(cropSrc, areaPixels);
      const d = await uploadRecitalPhoto(cropTarget, blob);
      setList((l) =>
        l.map((p) => (p.customer_id === cropTarget ? { ...p, ...d.row } : p)),
      );
      setCropSrc(null);
      setCropTarget(null);
      setOpenId(null);
      notify("保存しました");
    } catch (err) {
      notify(err.message || "保存に失敗しました", "error");
    } finally {
      setSaving(false);
    }
  };
  const cancelCrop = () => {
    if (saving) return;
    setCropSrc(null);
    setCropTarget(null);
  };

  // パンフレット出力：CSV＋画像ZIP を取得しブラウザでDL
  const exportPanphlet = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await exportRecitalPhotos();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "パンフレット素材_2026.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      notify("パンフレット用データを書き出しました");
    } catch (err) {
      notify(err.message || "出力に失敗しました", "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rc-page rc-page-wide">
      <Breadcrumbs breadcrumbs={RC_BREADCRUMBS} />

      <StatusMessage message={message} type={msgType} />

      {/* アップロード・差し替え共通の隠しファイル入力 */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        hidden
        onChange={onPickFile}
      />

      <div className="rc-el" data-verify-photos={loading ? "loading" : "ready"}>
        {/* PC: サマリー／検索／パンフレット出力 を1行。スマホは折り返し */}
        <div className="rc-ph-bar">
          <div className="rc-el-sum">
            <span>
              <b className="g">{done}</b>提出済み
            </span>
            <span className="sep">/</span>
            <span>
              <b className="r">{pending}</b>未提出
            </span>
            <span className="sep">/</span>
            <span>
              <b className="m">{list.length}</b>名
            </span>
          </div>

          <div className="rc-el-search">
            <i className="fa-thin fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="氏名・フリガナで検索"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button
            className="rc-btn pri rc-ph-export"
            onClick={exportPanphlet}
            disabled={exporting || list.length === 0}
          >
            {exporting ? "出力中…" : "パンフレット用ZIPをダウンロード"}
          </button>
        </div>

        {loading ? (
          <div className="rc-empty">読み込んでいます…</div>
        ) : rows.length === 0 ? (
          <div className="rc-empty">該当する参加者はいません</div>
        ) : (
          <div className="rc-pgrid">
            {rows.map((p) => (
              <div
                key={p.customer_id}
                className={`rc-pcard${p.submitted ? "" : " empty"}`}
                onClick={() => setOpenId(p.customer_id)}
              >
                {p.url ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img className="thumb" src={p.url} alt={p.name} />
                ) : (
                  <i className="fa-thin fa-user-plus"></i>
                )}
                <div className="cap">
                  {p.name}
                  {!p.submitted && (
                    <span className="rc-badge unpaid pcap">未提出</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="rc-modal-ov" onClick={cropSrc ? cancelCrop : closeModal}>
          <div className="rc-modal rc-photomodal" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              {cropSrc ? (
                "写真をトリミング"
              ) : (
                <span className="t">
                  {open.name}
                  {!open.submitted && <span className="rc-badge unpaid">未提出</span>}
                </span>
              )}
              {!saving && (
                <span className="x" onClick={cropSrc ? cancelCrop : closeModal}>
                  ✕
                </span>
              )}
            </div>

            <div className="mb">
              {cropSrc ? (
                /* ===== トリミングモード（3:4・同じモーダル内で切り替え） ===== */
                <>
                  <div className="rc-cropbox">
                    <Cropper
                      image={cropSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={ASPECT}
                      showGrid
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="rc-cropzoom">
                    <span>－</span>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.05}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                    />
                    <span>＋</span>
                  </div>
                  <p className="rc-crophint">
                    ドラッグ・拡大で位置を合わせ、3:4 に調整してください。
                  </p>
                  <div className="rc-cropacts">
                    <button className="rc-btn" onClick={cancelCrop} disabled={saving}>
                      キャンセル
                    </button>
                    <button className="rc-btn pri" onClick={confirmCrop} disabled={saving}>
                      {saving ? (
                        <>
                          <span className="rc-spin" />
                          保存中…
                        </>
                      ) : (
                        "保存する"
                      )}
                    </button>
                  </div>
                </>
              ) : (
                /* ===== 詳細モード ===== */
                <>
                  <div className="rc-ph-prev">
                    {open.url ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img src={open.url} alt={`${open.name} のパンフレット写真`} />
                    ) : (
                      <span className="empty">
                        <i className="fa-thin fa-image"></i>
                      </span>
                    )}
                  </div>

                  <dl>
                    <dt>フリガナ</dt>
                    <dd>{open.kana || "—"}</dd>
                  </dl>
                  <dl>
                    <dt>生年月日</dt>
                    <dd>
                      {fmtBirthday(open.birthday)}
                      {open.age != null && `（${open.age}歳）`}
                    </dd>
                  </dl>
                  <dl>
                    <dt>スタジオ</dt>
                    <dd>{open.studio || "—"}</dd>
                  </dl>
                  {open.submitted && (
                    <>
                      <dl>
                        <dt>提出日</dt>
                        <dd>{open.submitted_at || "—"}</dd>
                      </dl>
                      <dl>
                        <dt>提出元</dt>
                        <dd>{open.uploaded_by === "admin" ? "管理者（代理）" : "本人"}</dd>
                      </dl>
                    </>
                  )}
                  {!open.submitted && (
                    <p className="rc-ph-note">
                      本人の提出がありません。「アップロード」から管理者が登録できます。
                    </p>
                  )}

                  <div className="rc-ph-acts">
                    <button className="rc-btn pri" onClick={() => fileRef.current?.click()}>
                      {open.submitted ? "差し替え" : "アップロード"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
